import type {
  FlagAmountThresholdInput,
  FlagTypePolicyInput,
  FlagTypes,
} from '@generated/graphql';
import {
  EDITABLE_FLAG_PRIORITIES,
  getThresholdAxis,
} from '../utils/flagPolicy';

const hasDuplicateFlagTypes = (flagTypes: FlagTypes[]): boolean =>
  new Set(flagTypes).size !== flagTypes.length;

export const validateFlagPolicyOverrides = (
  flagTypePolicies: FlagTypePolicyInput[],
  flagAmountThresholds: FlagAmountThresholdInput[],
): string | null => {
  const policyTypes = flagTypePolicies.map((row) => row.flagType);
  if (hasDuplicateFlagTypes(policyTypes)) {
    return 'flagPolicies.validation.duplicatePolicy';
  }

  for (const row of flagTypePolicies) {
    const hasEnabled = row.enabled != null;
    const hasPriority = row.priority != null;
    if (!hasEnabled && !hasPriority) {
      return 'flagPolicies.validation.policyRowIncomplete';
    }
    if (
      hasPriority &&
      row.priority != null &&
      !EDITABLE_FLAG_PRIORITIES.includes(row.priority as any)
    ) {
      return 'flagPolicies.validation.invalidPriority';
    }
  }

  const thresholdTypes = flagAmountThresholds.map((row) => row.flagType);
  if (hasDuplicateFlagTypes(thresholdTypes)) {
    return 'flagPolicies.validation.duplicateThreshold';
  }

  for (const row of flagAmountThresholds) {
    const hasThresholdCents = row.thresholdCents != null;
    const hasPercentThreshold = row.percentThreshold != null;

    if (!hasThresholdCents && !hasPercentThreshold) {
      return 'flagPolicies.validation.thresholdRowIncomplete';
    }

    if (hasThresholdCents && row.thresholdCents! < 0) {
      return 'flagPolicies.validation.negativeThresholdCents';
    }

    if (
      hasPercentThreshold &&
      (row.percentThreshold! < 0 || row.percentThreshold! > 100)
    ) {
      return 'flagPolicies.validation.invalidPercentThreshold';
    }

    const axis = getThresholdAxis(row.flagType);
    if (axis === 'PERCENT_ONLY' && hasThresholdCents) {
      return 'flagPolicies.validation.percentOnlyNoCents';
    }
  }

  return null;
};

export const normalizePolicyOverrides = (
  rows: FlagTypePolicyInput[],
): FlagTypePolicyInput[] =>
  rows.map((row) => ({
    flagType: row.flagType,
    ...(row.enabled != null ? { enabled: row.enabled } : {}),
    ...(row.priority != null ? { priority: row.priority } : {}),
  }));

export const normalizeThresholdOverrides = (
  rows: FlagAmountThresholdInput[],
): FlagAmountThresholdInput[] =>
  rows.map((row) => ({
    flagType: row.flagType,
    ...(row.thresholdCents != null
      ? { thresholdCents: row.thresholdCents }
      : {}),
    ...(row.percentThreshold != null
      ? { percentThreshold: row.percentThreshold }
      : {}),
  }));

const sortByFlagType = <T extends { flagType: FlagTypes }>(rows: T[]): T[] =>
  [...rows].sort((a, b) => a.flagType.localeCompare(b.flagType));

/** Compare policy + threshold override arrays for dirty detection. */
export const flagPolicyOverridesEqual = (
  policiesA: FlagTypePolicyInput[],
  policiesB: FlagTypePolicyInput[],
  thresholdsA: FlagAmountThresholdInput[],
  thresholdsB: FlagAmountThresholdInput[],
): boolean =>
  JSON.stringify(sortByFlagType(normalizePolicyOverrides(policiesA))) ===
    JSON.stringify(sortByFlagType(normalizePolicyOverrides(policiesB))) &&
  JSON.stringify(sortByFlagType(normalizeThresholdOverrides(thresholdsA))) ===
    JSON.stringify(sortByFlagType(normalizeThresholdOverrides(thresholdsB)));
